/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { createAction, props } from '@ngrx/store';
import { GetSegmentInfoResponseDto } from 'customer-speed-api';
import { LatLng } from '../../../core/models';
import { EPanelDrawerState } from '../../../replay/models';
import {
  EManagePostedSpeedTabs,
  IFilters,
  IPostedSpeedUpdate,
  IRoadDetails,
  ISaveOverrideTrackingPayload,
  IUpdateSpeedLimitForm
} from '../../models';

export const triggerManagePostedSpeed = createAction('[ManagePostedSpeed] Trigger Manage Posted Speed', props<{ mapCenter: LatLng }>());

// Panel Management
export const openManagePostedSpeedPanel = createAction(
  '[ManagePostedSpeed] Open Manage Posted Speed Panel',
  props<{ mapCenter: LatLng }>()
);

export const openManagePostedSpeedPanelFromDeeplink = createAction(
  '[ManagePostedSpeed] Open Manage Posted Speed Panel From Deeplink',
  props<{ coordinates: LatLng }>()
);

export const closeManagePostedSpeedPanel = createAction('[ManagePostedSpeed] Close Manage Posted Speed Panel');

// Tab Management
export const setActiveTab = createAction('[ManagePostedSpeed] Set Active Tab', props<{ tab: EManagePostedSpeedTabs }>());

// Load Overrides
export const loadOverrides = createAction('[ManagePostedSpeed] Load Overrides');
export const loadOverridesSuccess = createAction(
  '[ManagePostedSpeed] Load Overrides Success',
  props<{ overrides: IPostedSpeedUpdate[]; totalItems: number; totalAccountOverrides: number }>()
);
export const loadOverridesFailure = createAction('[ManagePostedSpeed] Load Overrides Failure', props<{ error: any }>());

// Filters
export const applyFilters = createAction('[ManagePostedSpeed] Apply Filters', props<{ filters: IFilters }>());
export const clearFilters = createAction('[ManagePostedSpeed] Clear Filters');
export const setAllowMapRefine = createAction('[ManagePostedSpeed] Set Allow Map Refine', props<{ allowMapRefine: boolean }>());

// Posted Speed Updates
export const updatePostedSpeed = createAction('[ManagePostedSpeed] Update Posted Speed');

export const closeUpdateSpeedLimitForm = createAction('[ManagePostedSpeed] Close Update Speed Limit Form');

// Save Override (Create or Update)
export const saveOverride = createAction('[ManagePostedSpeed] Save Override', props<{ formData: IUpdateSpeedLimitForm }>());
export const saveOverrideSuccess = createAction(
  '[ManagePostedSpeed] Save Override Success',
  props<{ trackingPayload: ISaveOverrideTrackingPayload }>()
);
export const saveOverrideFailure = createAction('[ManagePostedSpeed] Save Override Failure', props<{ error: any }>());

// Form field changes
export const updateSpeedLimitFormField = createAction(
  '[ManagePostedSpeed] Update Speed Limit Form Field',
  props<{ field: Partial<IUpdateSpeedLimitForm> }>()
);

export const clearSpeedLimitForm = createAction('[ManagePostedSpeed] Clear Speed Limit Form');

// Edit/Delete Operations
export const editPostedSpeedUpdate = createAction('[ManagePostedSpeed] Edit Posted Speed Update', props<{ update: IPostedSpeedUpdate }>());

export const deletePostedSpeedUpdate = createAction('[ManagePostedSpeed] Delete Posted Speed Update', props<{ updateId: string }>());
export const deletePostedSpeedUpdateSuccess = createAction(
  '[ManagePostedSpeed] Delete Posted Speed Update Success',
  props<{ updateId: string }>()
);
export const deletePostedSpeedUpdateFailure = createAction(
  '[ManagePostedSpeed] Delete Posted Speed Update Failure',
  props<{ error: any }>()
);

// Drawer State Management
export const updateDrawerState = createAction('[ManagePostedSpeed] Update Drawer State', props<{ state: EPanelDrawerState }>());

// Segment Info (placeholder for future API)
export const getSegmentInfo = createAction('[ManagePostedSpeed] Get Segment Info', props<{ mapCenter: LatLng }>());
export const getSegmentInfoSuccess = createAction(
  '[ManagePostedSpeed] Get Segment Info Success',
  props<{ mapCenter: LatLng; roadDetails: IRoadDetails; segmentInfo?: GetSegmentInfoResponseDto }>()
);
export const getSegmentInfoFailure = createAction(
  '[ManagePostedSpeed] Get Segment Info Failure',
  props<{ error: any; roadNotAvailable: boolean }>()
);

// Override Conflict
export type TOverrideConflictSource = 'selection' | 'save';

export const showOverrideConflictDialog = createAction(
  '[ManagePostedSpeed] Show Override Conflict Dialog',
  props<{ segmentInfo: GetSegmentInfoResponseDto; source: TOverrideConflictSource }>()
);

// Segment Selection
export const clearSegmentSelection = createAction('[ManagePostedSpeed] Clear Segment Selection');

// Undo/Redo Segment Selection
export const undoSegmentSelection = createAction('[ManagePostedSpeed] Undo Segment Selection');
export const redoSegmentSelection = createAction('[ManagePostedSpeed] Redo Segment Selection');

// Card Hover & Selection
export const hoverUpdateCard = createAction('[ManagePostedSpeed] Hover Update Card', props<{ updateId: string | null }>());
export const selectUpdateCard = createAction('[ManagePostedSpeed] Select Update Card', props<{ updateId: string | null }>());

// Multi-Segment Selection
export const selectAdditionalSegment = createAction('[ManagePostedSpeed] Select Additional Segment', props<{ mapCenter: LatLng }>());

export const addSegmentToSelection = createAction(
  '[ManagePostedSpeed] Add Segment To Selection',
  props<{ segmentInfo: GetSegmentInfoResponseDto; roadDetails: IRoadDetails }>()
);

export const addSegmentToSelectionFailure = createAction(
  '[ManagePostedSpeed] Add Segment To Selection Failure',
  props<{ error: any; roadNotAvailable: boolean }>()
);

export const removeSegmentFromSelection = createAction('[ManagePostedSpeed] Remove Segment From Selection', props<{ segmentId: string }>());

export const removeSegmentGroup = createAction('[ManagePostedSpeed] Remove Segment Group', props<{ groupId: string }>());

export const showRoadTypeConflictDialog = createAction('[ManagePostedSpeed] Show Road Type Conflict Dialog');
